import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTable, useSortBy, useFlexLayout } from 'react-table';
import {
  Header,
  Body as TableBody,
  Row,
} from '~/components/DataTables/styling';
import { HeaderCell, RowCell } from '~/components/DataTables';
import TEST_ID from './index.testid';
import { columns } from './constants';
import { SortDirection } from '~/graphql/types';
import { ACTION_COLUMN_ACCESSOR } from '~/components/DataTables/util/TableHeaderCell/action';
import { ascend, descend, prop, sort } from 'ramda';
import ActionColumnCell from '~/components/DataTables/components/ActionColumnCell';
import useConfirmModal from '~/components/Alerts/useConfirmModal';

export type Row = {
  name: string;
  fields: string;
};

export type Props = {
  data: Array<Row>;
  onDelete: (id: string) => void;
  onCopy: (id: string) => void;
  onEdit: (id: string) => void;
  loading: boolean;
};

const defaultColumn = {
  minWidth: 20,
  width: 120,
  maxWidth: 200,
};
const defaultSortColumn = 'name';

const confirmDeleteModalText = {
  title: 'Koppeling verwijderen',
  message: 'Weet je zeker dat je deze koppeling wilt verwijderen?',
  buttonConfirmTitle: 'Verwijderen',
  buttonCancelTitle: 'Annuleren',
};

const confirmCopyModalText = {
  title: 'Koppeling kopiëren?',
  message: 'Weet je zeker dat je deze koppeling wilt kopiëren?',
  buttonConfirmTitle: 'Kopiëren',
  buttonCancelTitle: 'Annuleren',
};

const Table: React.FC<Props> = ({
  data,
  onDelete,
  onCopy,
  onEdit,
  loading,
  ...rest
}) => {
  const [sortedItems, setSortedItems] = useState<Array<Row>>(
    sort(descend(prop(defaultSortColumn)), data),
  );

  const onSort = (dataProperty: keyof Row, sortDirection: SortDirection) => {
    const direction = sortDirection === SortDirection.Desc ? descend : ascend;
    const sortFn = direction(prop(dataProperty));
    const sorted = sort(sortFn, data);
    setSortedItems(sorted);
  };

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns: columns(onSort),
        data: sortedItems,
        defaultColumn,
        disableMultiSort: true,
        autoResetSortBy: false,
        autoResetHiddenColumns: false,
        getRowId: ({ id }) => id,
        manualSortBy: true,
        disableSortRemove: true,
        initialState: {
          sortBy: [{ id: defaultSortColumn, desc: true }],
        },
      },
      useFlexLayout,
      useSortBy,
    );

  useEffect(() => {
    if (data.length !== sortedItems.length) {
      setSortedItems(sort(descend(prop(defaultSortColumn)), data));
    }
  }, [data, sortedItems]);

  const [showDeleteConfirmModal, setOnDeleteConfirmFunction] = useConfirmModal(
    confirmDeleteModalText,
    () => {},
    () => {},
    TEST_ID.CONFIRM_MODAL,
    false,
    'destructive',
  );

  const [showCopyConfirmModal, setOnCopyConfirmFunction] = useConfirmModal(
    confirmCopyModalText,
    () => {},
    () => {},
    TEST_ID.CONFIRM_MODAL,
    false,
    'normal',
  );

  return (
    <Container {...getTableProps()} {...rest} data-testid={TEST_ID.CONTAINER}>
      <Header.Large>
        {headerGroups.map(headerGroup => {
          const headerGroupProps = { ...headerGroup.getHeaderGroupProps() };
          return (
            <StyledHeaderTr
              key={headerGroupProps.key}
              data-testid={TEST_ID.HEADER_ROW}
            >
              {headerGroup.headers.map((column, idx) => {
                const isLastColumn = idx === headerGroup.headers.length - 1;
                const headerCellKey = `header-${idx}`;

                return (
                  <HeaderCell
                    key={headerCellKey}
                    column={column}
                    isLastColumn={isLastColumn}
                  />
                );
              })}
            </StyledHeaderTr>
          );
        })}
      </Header.Large>
      <TableBody.Standard.Outer>
        <TableBody.Standard.Inner {...getTableBodyProps()}>
          {rows.map(row => {
            prepareRow(row);

            const rowProps = { ...row.getRowProps() };
            const eventId = row.id;

            return (
              <Row.Standard
                {...rowProps}
                key={rowProps.key}
                data-objectid={eventId}
                dataTestid={TEST_ID.LIST_ITEM}
                to={
                  eventId.startsWith('ze')
                    ? `/-/apps/zapier/events/${eventId}`
                    : `/-/apps/zapier/triggers/${eventId}`
                }
              >
                {row.cells.map((cell, idx) => {
                  const isLastColumn = idx === row.cells.length - 1;

                  if (cell.column.id === ACTION_COLUMN_ACCESSOR) {
                    return (
                      <ActionColumnCell
                        key={'event-action-column-cell'}
                        loading={loading}
                        options={[
                          {
                            label: 'Aanpassen',
                            onClick: () => onEdit(cell.row.id),
                            key: 'edit',
                          },
                          {
                            label: 'Kopiëren',
                            onClick: () => {
                              const copyFn = () => onCopy(cell.row.id);
                              setOnCopyConfirmFunction(copyFn);
                              showCopyConfirmModal();
                            },
                            key: 'copy',
                          },
                          {
                            label: 'Verwijderen',
                            onClick: () => {
                              const deleteFn = () => onDelete(cell.row.id);
                              setOnDeleteConfirmFunction(deleteFn);
                              showDeleteConfirmModal();
                            },
                            key: 'delete',
                            type: 'DELETE',
                          },
                        ]}
                        cell={cell}
                      />
                    );
                  }

                  return (
                    <RowCell
                      key={`cell-${idx}`}
                      cell={cell}
                      isLastColumn={isLastColumn}
                    />
                  );
                })}
              </Row.Standard>
            );
          })}
        </TableBody.Standard.Inner>
      </TableBody.Standard.Outer>
    </Container>
  );
};

const Container = styled.div<{}>`
  width: 100%;
`;

const StyledHeaderTr = styled.div<{}>`
  white-space: nowrap;
  display: flex;
`;

export default Table;
